/**
 * Onboarding — LIVE first-run guided setup (web).
 * Route: /onboarding  (shown once for new subscribers; skippable everywhere).
 *
 * Steps: Welcome → Persona → Confirm details → Add contractors → W-9/COI → Done.
 * Saves persona + business details, creates contractors, then marks
 * onboarding_completed via POST /api/onboarding/complete.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Rocket, CheckCircle2, Building2, HardHat, User, Briefcase, Users, ShieldCheck,
  Mail, Upload, ArrowRight, ArrowLeft, X, Check, Landmark, Loader2, Plus, Link2, Trash2,
  FileSpreadsheet, FileText,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const NAVY = '#0b3b6f';
const GREEN = '#5c9e33';

const STEPS = [
  { key: 'welcome', label: 'Welcome' },
  { key: 'persona', label: 'Who you are' },
  { key: 'details', label: 'Your details' },
  { key: 'lenders', label: 'Your lenders' },
  { key: 'contractors', label: 'Add contractors' },
  { key: 'compliance', label: 'W-9 & COI' },
  { key: 'done', label: 'All set' },
];

function BrandMark({ size = 40 }) {
  return (
    <div className="flex items-center justify-center font-extrabold text-white shadow-sm"
      style={{ width: size, height: size, background: '#2563EB', borderRadius: size * 0.26, fontSize: size * 0.52, lineHeight: 1 }}>D</div>
  );
}

export default function Onboarding({ user, onUpdateUser }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [persona, setPersona] = useState(user?.persona || 'developer');
  const [saving, setSaving] = useState(false);
  const firstName = user?.first_name || (user?.name || '').split(' ')[0] || 'there';

  const [details, setDetails] = useState({
    name: user?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
    phone: user?.phone || '',
    business_name: user?.business_name || user?.company_brand_name || '',
    tax_id: user?.tax_id || '',
  });
  const isBusiness = !!(user?.business_name || user?.company_brand_name);

  const [contractors, setContractors] = useState([]);
  const [draft, setDraft] = useState({ company: '', email: '', trade: '' });
  const [addingContractor, setAddingContractor] = useState(false);
  const [lenders, setLenders] = useState([]);

  const pct = Math.round((step / (STEPS.length - 1)) * 100);
  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  async function saveDetails() {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/api/auth/profile/${user.id}`, {
        name: details.name || undefined,
        phone: details.phone || undefined,
        business_name: details.business_name || undefined,
        tax_id: details.tax_id || undefined,
      });
      onUpdateUser?.(data);
      toast.success('Details saved');
      next();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save details');
    } finally { setSaving(false); }
  }

  async function addContractor() {
    if (!draft.company.trim() || !draft.email.trim()) { toast.error('Company and email are required'); return; }
    setAddingContractor(true);
    try {
      const { data } = await axios.post(`${API}/api/contractors`, {
        user_id: user.id, company: draft.company.trim(), email: draft.email.trim(),
        trade: draft.trade.trim() || undefined,
      });
      setContractors((c) => [...c, { id: data.id, company: data.company, email: data.email, trade: data.trade }]);
      setDraft({ company: '', email: '', trade: '' });
      toast.success(`Added ${data.company}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add contractor');
    } finally { setAddingContractor(false); }
  }

  async function finish() {
    setSaving(true);
    localStorage.setItem('onboarding_done', '1'); // gate flag, set before navigation
    try {
      const { data } = await axios.post(`${API}/api/onboarding/complete?user_id=${user.id}`, { persona });
      onUpdateUser?.(data);
      toast.success('You’re all set!');
      navigate('/dashboard', { replace: true });
    } catch (e) {
      toast.error('Could not finish setup, but you can continue.');
      navigate('/dashboard', { replace: true });
    } finally { setSaving(false); }
  }

  return (
    <div className="min-h-screen flex" style={{ background: '#f1f5f9' }} data-testid="onboarding">
      {/* Left rail */}
      <aside className="hidden lg:flex flex-col w-72 flex-shrink-0 p-8 text-white" style={{ background: NAVY }}>
        <div className="mb-10">
          <img src="/logo-demo-white.png" alt="DrawReady" className="h-8 w-auto" />
          <p className="text-[11px] text-blue-200 mt-2">Construction Finance</p>
        </div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-blue-300 mb-4">Getting started</p>
        <ol className="space-y-1.5 flex-1">
          {STEPS.map((s, i) => {
            const done = i < step; const active = i === step;
            return (
              <li key={s.key} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition ${active ? 'bg-white/12' : ''}`}>
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                  style={{ background: done ? GREEN : active ? '#fff' : 'rgba(255,255,255,0.12)', color: done ? '#fff' : active ? NAVY : '#cbd5e1' }}>
                  {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </span>
                <span className={`text-sm ${active ? 'font-bold text-white' : done ? 'text-blue-200' : 'text-blue-300/70'}`}>{s.label}</span>
              </li>
            );
          })}
        </ol>
        <div className="mt-6">
          <div className="h-1.5 rounded-full bg-white/15 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: GREEN }} /></div>
          <p className="text-[11px] text-blue-200 mt-2">{pct}% complete · takes ~3 min</p>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between px-6 sm:px-10 py-4 border-b border-slate-200 bg-white">
          <div className="lg:hidden"><img src="/logo-full.png" alt="DrawReady" className="h-7 w-auto" /></div>
          <span className="hidden lg:block text-sm text-slate-400">Step {step + 1} of {STEPS.length}</span>
          {step > 0 && step < STEPS.length - 1 && (
            <button className="text-sm font-semibold text-slate-400 hover:text-slate-700 inline-flex items-center gap-1" onClick={next} data-testid="ob-skip">
              Skip for now <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-6 sm:px-10 py-10">
            {step === 0 && <Welcome firstName={firstName} />}
            {STEPS[step].key === 'persona' && <Persona persona={persona} setPersona={setPersona} />}
            {STEPS[step].key === 'details' && <Details details={details} setDetails={setDetails} isBusiness={isBusiness} />}
            {STEPS[step].key === 'lenders' && <LendersStep user={user} lenders={lenders} setLenders={setLenders} />}
            {STEPS[step].key === 'contractors' && <Contractors contractors={contractors} draft={draft} setDraft={setDraft} add={addContractor} adding={addingContractor} setContractors={setContractors} />}
            {STEPS[step].key === 'compliance' && <Compliance contractors={contractors} navigate={navigate} />}
            {STEPS[step].key === 'done' && <Done firstName={firstName} contractors={contractors} lenders={lenders} />}
          </div>
        </div>

        <div className="border-t border-slate-200 bg-white px-6 sm:px-10 py-4 flex items-center justify-between">
          <button onClick={back} disabled={step === 0}
            className={`inline-flex items-center gap-1.5 text-sm font-semibold ${step === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:text-slate-900'}`} data-testid="ob-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step === 2 ? (
            <Button onClick={saveDetails} disabled={saving} className="text-white" style={{ background: NAVY }} data-testid="ob-next">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Save & continue <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : step < STEPS.length - 1 ? (
            <Button onClick={next} className="text-white" style={{ background: NAVY }} data-testid="ob-next">
              {step === 0 ? 'Get started' : 'Continue'} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button onClick={finish} disabled={saving} className="text-white" style={{ background: GREEN }} data-testid="ob-finish">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Rocket className="w-4 h-4 mr-1.5" />} Go to my dashboard
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

function Welcome({ firstName }) {
  return (
    <div className="text-center pt-6" data-testid="ob-step-welcome">
      <div className="mx-auto mb-5 flex justify-center"><BrandMark size={64} /></div>
      <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: GREEN }}>Welcome to DrawReady</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2">Let’s get your account set up, {firstName}</h1>
      <p className="text-slate-500 mt-3 max-w-lg mx-auto text-[15px] leading-relaxed">
        A few quick steps so DrawReady works the way you do — confirm your details, bring in your contractors,
        and collect the compliance docs banks ask for. You can skip anything and finish it later.
      </p>
      <div className="grid sm:grid-cols-3 gap-3 mt-8 text-left">
        {[{ icon: User, t: 'Confirm your info', s: 'Business & contact details' },
          { icon: Users, t: 'Add contractors', s: 'Your trusted trades' },
          { icon: ShieldCheck, t: 'Collect W-9 / COI', s: 'Bank-ready compliance' }].map((c) => (
          <div key={c.t} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center mb-2"><c.icon className="w-5 h-5" style={{ color: NAVY }} /></div>
            <p className="text-sm font-bold text-slate-900">{c.t}</p>
            <p className="text-xs text-slate-500 mt-0.5">{c.s}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Persona({ persona, setPersona }) {
  const OPTIONS = [
    { key: 'developer', icon: Building2, t: 'Developer / Builder', s: 'I build and manage my own projects and financing.' },
    { key: 'gc', icon: HardHat, t: 'General Contractor', s: 'I manage builds & draws on behalf of owners/clients.' },
  ];
  return (
    <div data-testid="ob-step-persona">
      <StepHeader eyebrow="A quick question" title="How do you use DrawReady?" sub="This tailors a few labels. You can change it anytime in Settings." />
      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        {OPTIONS.map((o) => {
          const active = persona === o.key;
          return (
            <button key={o.key} onClick={() => setPersona(o.key)}
              className={`text-left rounded-2xl border-2 p-5 transition ${active ? 'bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
              style={active ? { borderColor: NAVY } : {}} data-testid={`ob-persona-${o.key}`}>
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: active ? NAVY : '#eff6ff' }}><o.icon className="w-6 h-6" style={{ color: active ? '#fff' : NAVY }} /></div>
                {active && <CheckCircle2 className="w-5 h-5" style={{ color: NAVY }} />}
              </div>
              <p className="text-base font-bold text-slate-900 mt-3">{o.t}</p>
              <p className="text-sm text-slate-500 mt-1">{o.s}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Details({ details, setDetails, isBusiness }) {
  const set = (k) => (e) => setDetails((d) => ({ ...d, [k]: e.target.value }));
  return (
    <div data-testid="ob-step-details">
      <StepHeader eyebrow="Confirm your details" title="Does this look right?" sub="We pulled this from your sign-up. Tweak anything, or skip — you can edit it later in Settings." />
      <div className="mt-6 space-y-4">
        <Field label="Full name" value={details.name} onChange={set('name')} testid="ob-name" />
        <Field label="Phone" value={details.phone} onChange={set('phone')} testid="ob-phone" />
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-4"><Briefcase className="w-4 h-4" style={{ color: GREEN }} /> Business details</p>
          <div className="space-y-4">
            <Field label="Company name" value={details.business_name} onChange={set('business_name')} testid="ob-company" />
            <Field label="EIN (optional)" value={details.tax_id} onChange={set('tax_id')} placeholder="12-3456789" testid="ob-ein" hint={isBusiness ? 'Optional — helps auto-fill 1099s later.' : 'Optional'} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Contractors({ contractors, draft, setDraft, add, adding, setContractors }) {
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  return (
    <div data-testid="ob-step-contractors">
      <StepHeader eyebrow="Your team" title="Add your contractors" sub="Bring in the trades you work with. You can also do this later from the Contractors page." />
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {contractors.map((r) => (
          <div key={r.id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center"><HardHat className="w-4 h-4" style={{ color: GREEN }} /></div>
            <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-slate-900 truncate">{r.company}</p><p className="text-[11px] text-slate-400 truncate">{r.trade ? `${r.trade} · ` : ''}{r.email}</p></div>
            <button onClick={() => setContractors((c) => c.filter((x) => x.id !== r.id))} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <div className="grid sm:grid-cols-[1fr_1fr_0.7fr_auto] gap-2 p-4 bg-slate-50">
          <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Company name" value={draft.company} onChange={set('company')} data-testid="ob-contractor-company" />
          <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Email" value={draft.email} onChange={set('email')} data-testid="ob-contractor-email" />
          <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Trade" value={draft.trade} onChange={set('trade')} data-testid="ob-contractor-trade" />
          <button onClick={add} disabled={adding} className="inline-flex items-center justify-center gap-1.5 text-sm font-bold text-white rounded-lg px-4 py-2" style={{ background: NAVY }} data-testid="ob-contractor-add">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
          </button>
        </div>
      </div>
      {contractors.length > 0 && <p className="text-xs text-emerald-600 font-semibold mt-3">{contractors.length} contractor{contractors.length > 1 ? 's' : ''} added</p>}
    </div>
  );
}

function Compliance({ contractors, navigate }) {
  return (
    <div data-testid="ob-step-compliance">
      <StepHeader eyebrow="Bank-ready compliance" title="Collect W-9 & COI" sub="Two ways per contractor: send a secure link so they upload their own, or upload the files on their behalf. Banks ask for these before funding." />
      {contractors.length > 0 ? (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {contractors.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0">
              <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-slate-900 truncate">{r.company}</p><p className="text-[11px] text-slate-400 truncate">{r.email}</p></div>
              <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-slate-50 text-slate-500 border border-slate-200">W-9 · COI pending</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">Add contractors first to request their compliance docs.</div>
      )}
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <button onClick={() => navigate('/contractors')} className="rounded-2xl border border-slate-200 bg-white p-4 text-left hover:border-slate-300" data-testid="ob-compliance-invite">
          <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center mb-2"><Link2 className="w-5 h-5" style={{ color: NAVY }} /></div>
          <p className="text-sm font-bold text-slate-900">Send an upload link</p>
          <p className="text-xs text-slate-500 mt-0.5">Contractor uploads their own W-9 & COI via a secure portal.</p>
        </button>
        <button onClick={() => navigate('/contractors')} className="rounded-2xl border border-slate-200 bg-white p-4 text-left hover:border-slate-300" data-testid="ob-compliance-upload">
          <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center mb-2"><Upload className="w-5 h-5" style={{ color: GREEN }} /></div>
          <p className="text-sm font-bold text-slate-900">Upload on their behalf</p>
          <p className="text-xs text-slate-500 mt-0.5">Already have the files? Add them from the Contractors page.</p>
        </button>
      </div>

      <p className="text-[11px] text-slate-400 mt-3">Both options open your Contractors workspace where documents are collected & tracked.</p>
    </div>
  );
}

function LendersStep({ user, lenders, setLenders }) {
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null); // `${lenderId}:${formType}`

  const addLender = async () => {
    if (!name.trim()) { toast.error('Enter a lender name'); return; }
    setAdding(true);
    try {
      const { data } = await apiService.createLender({ user_id: user.id, name: name.trim() });
      setLenders((l) => [...l, data]);
      setName('');
      toast.success(`Added ${data.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add lender');
    } finally { setAdding(false); }
  };

  const upload = async (lenderId, formType, file) => {
    if (!file) return;
    setBusy(`${lenderId}:${formType}`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await apiService.uploadLenderForm(lenderId, formType, fd);
      setLenders((ls) => ls.map((l) => (l.id === lenderId ? data : l)));
      toast.success('Form uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(null); }
  };

  const FORMS = [
    { type: 'bid_sheet', label: 'Bid sheet', icon: FileSpreadsheet, field: 'bid_sheet_file' },
    { type: 'draw_form', label: 'Draw form', icon: FileText, field: 'draw_form_file' },
  ];

  return (
    <div data-testid="ob-step-lenders">
      <StepHeader eyebrow="Your banks" title="Add your lenders" sub="Add each bank you work with and upload their bid sheet & draw request form. DrawReady uses these exact forms so your bids & draws stay compliant. You can do this later in Lenders." />
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {lenders.map((l) => (
          <div key={l.id} className="px-4 py-3 border-b border-slate-100 last:border-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Landmark className="w-4 h-4" style={{ color: NAVY }} /></div>
              <p className="flex-1 text-sm font-semibold text-slate-900 truncate">{l.name}</p>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 pl-12">
              {FORMS.map((f) => {
                const has = !!l[f.field];
                const b = busy === `${l.id}:${f.type}`;
                return (
                  <label key={f.type} className="cursor-pointer">
                    <input type="file" className="hidden" accept=".pdf,.xlsx,.xlsm,.xls,.doc,.docx" disabled={b}
                      onChange={(e) => { upload(l.id, f.type, e.target.files?.[0]); e.target.value = ''; }}
                      data-testid={`ob-upload-${f.type}-${l.id}`} />
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg border px-2.5 py-1.5 ${has ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                      {b ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : has ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />}
                      {has ? `${f.label} \u2713` : `Upload ${f.label}`}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
        <div className="flex gap-2 p-4 bg-slate-50">
          <input className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Lender name (e.g. Hills Bank)" value={name}
            onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addLender()} data-testid="ob-lender-name" />
          <button onClick={addLender} disabled={adding} className="inline-flex items-center justify-center gap-1.5 text-sm font-bold text-white rounded-lg px-4 py-2" style={{ background: NAVY }} data-testid="ob-lender-add">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
          </button>
        </div>
      </div>
      {lenders.length > 0 && <p className="text-xs text-emerald-600 font-semibold mt-3">{lenders.length} lender{lenders.length > 1 ? 's' : ''} added</p>}
    </div>
  );
}


function Done({ firstName, contractors, lenders = [] }) {
  const recap = [
    { t: 'Account details confirmed', ok: true },
    { t: `${lenders.length} lender${lenders.length === 1 ? '' : 's'} added`, ok: lenders.length > 0 },
    { t: `${contractors.length} contractor${contractors.length === 1 ? '' : 's'} added`, ok: contractors.length > 0 },
    { t: 'W-9 / COI collection', ok: false },
  ];
  return (
    <div className="text-center pt-6" data-testid="ob-step-done">
      <div className="mx-auto mb-5 flex justify-center"><BrandMark size={64} /></div>
      <h1 className="text-3xl font-extrabold text-slate-900">You’re all set, {firstName}</h1>
      <p className="text-slate-500 mt-2 max-w-md mx-auto">Anything you skipped stays on your dashboard checklist so you can finish it whenever.</p>
      <div className="mt-7 rounded-2xl border border-slate-200 bg-white p-5 text-left max-w-md mx-auto">
        {recap.map((r) => (
          <div key={r.t} className="flex items-center gap-3 py-2">
            {r.ok ? <CheckCircle2 className="w-5 h-5" style={{ color: GREEN }} /> : <span className="w-5 h-5 rounded-full border-2 border-amber-300 flex items-center justify-center text-[10px] font-bold text-amber-500">!</span>}
            <span className={`text-sm ${r.ok ? 'text-slate-700' : 'text-slate-500'}`}>{r.t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepHeader({ eyebrow, title, sub }) {
  return (
    <div>
      <div className="mb-4"><BrandMark size={36} /></div>
      <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: GREEN }}>{eyebrow}</p>
      <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">{title}</h1>
      {sub && <p className="text-slate-500 mt-2 text-[15px] leading-relaxed">{sub}</p>}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, hint, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500">{label}</label>
      <input value={value} onChange={onChange} placeholder={placeholder} data-testid={testid}
        className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-200" />
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}
